import { useEffect, useState } from 'react';
import * as API from '../../API';
import type InvoicedOrder from '../../Models/InvoicedOrder';

interface UseOrdersForBatchResult {
    orders: InvoicedOrder[];
    loading: boolean;
}

interface ApiResponse {
    AllOk: boolean;
    Orders: InvoicedOrder[];
}

/**
 * Custom hook to fetch orders for a specific invoicing batch.
 * Only fetches when shouldFetch is true
 *
 * @param batchId - The ID of the batch to fetch orders for
 * @param shouldFetch - Whether to fetch the orders (typically controlled by expand state)
 * @returns Object containing orders array, loading state, and error state
 */
export function useOrdersForBatch(batchId: number, shouldFetch: boolean): UseOrdersForBatchResult {
    const [orders, setOrders] = useState<InvoicedOrder[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!shouldFetch) {
            setLoading(false);
            return;
        }

        setLoading(true);

        API.eFact.getOrdersForPreviousInvoicingBatch(batchId)
            .then((res: ApiResponse) => {
                if (!res.AllOk) {
                    console.error('Issue with getOrdersForPreviousInvoicingBatch:', res);
                    setLoading(false);
                    return;
                }

                setOrders(res.Orders);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Could not get orders linked to previous invoicing batch:', err);
                setLoading(false);
            });
    }, [batchId, shouldFetch]);

    return { orders, loading };
}
